
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Venue } from "@/types/voucher";

type Coordinates = {
  latitude: number;
  longitude: number;
};

export type VenueWithCoordinates = Venue & {
  latitude: number;
  longitude: number;
};

export function useVenues() {
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch venues from Supabase
  const { data: venues = [], isLoading, error, refetch } = useQuery({
    queryKey: ['venues'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('venues')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data as Venue[];
    }
  });

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (coord1: Coordinates, coord2: Coordinates): number => {
    const toRadian = (degree: number) => degree * Math.PI / 180;
    
    const lat1 = toRadian(coord1.latitude);
    const lon1 = toRadian(coord1.longitude);
    const lat2 = toRadian(coord2.latitude);
    const lon2 = toRadian(coord2.longitude);
    
    // Haversine formula
    const dlon = lon2 - lon1;
    const dlat = lat2 - lat1;
    const a = Math.sin(dlat/2)**2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlon/2)**2;
    const c = 2 * Math.asin(Math.sqrt(a));
    
    // Radius of Earth in kilometers
    const r = 6371;
    
    // Calculate distance
    return c * r;
  };

  // Check if user is at venue (within certain distance)
  const isUserAtVenue = (
    userLocation: Coordinates, 
    venue: Venue & { latitude: number; longitude: number }, 
    maxDistanceKm = 0.2
  ): boolean => {
    if (!venue.latitude || !venue.longitude) return false;
    
    const distance = calculateDistance(
      userLocation,
      { latitude: venue.latitude, longitude: venue.longitude }
    );
    
    return distance <= maxDistanceKm;
  };

  // Find the nearest venue to the user
  const findNearestVenue = (userLocation: Coordinates): Venue | null => {
    if (!venues.length) return null;
    
    const venuesWithCoords = venues.filter(v => v.latitude && v.longitude);
    if (!venuesWithCoords.length) return null;
    
    let nearestVenue = venuesWithCoords[0];
    let minDistance = calculateDistance(
      userLocation,
      { latitude: nearestVenue.latitude!, longitude: nearestVenue.longitude! }
    );
    
    for (const venue of venuesWithCoords.slice(1)) {
      const distance = calculateDistance(
        userLocation,
        { latitude: venue.latitude!, longitude: venue.longitude! }
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        nearestVenue = venue;
      }
    }
    
    return nearestVenue;
  };

  // Add a new venue
  const addVenue = async (venueData: Omit<Venue, 'id' | 'created_at'>) => {
    try {
      setIsProcessing(true);
      
      const { data, error } = await supabase
        .from('venues')
        .insert(venueData)
        .select()
        .single();
      
      if (error) throw error;
      
      toast.success("Venue added successfully");
      await refetch();
      return data;
    } catch (error) {
      console.error("Error adding venue:", error);
      toast.error("Failed to add venue");
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  // Update an existing venue
  const updateVenue = async (id: string, venueData: Partial<Omit<Venue, 'id' | 'created_at'>>) => {
    try {
      setIsProcessing(true);
      
      const { data, error } = await supabase
        .from('venues')
        .update(venueData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      toast.success("Venue updated successfully");
      await refetch();
      return data;
    } catch (error) {
      console.error("Error updating venue:", error);
      toast.error("Failed to update venue");
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete a venue
  const deleteVenue = async (id: string) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('venues')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast.success("Venue deleted successfully");
      await refetch();
      return true;
    } catch (error) {
      console.error("Error deleting venue:", error);
      toast.error("Failed to delete venue");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    venues,
    isLoading,
    error,
    isProcessing,
    addVenue,
    updateVenue,
    deleteVenue,
    isUserAtVenue,
    findNearestVenue,
    refetch
  };
}
